import { useEffect, useState } from "react";
import { Plus, FlaskConical, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Spinner, ErrorNote } from "@/components/PortalPage";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AddMachineDialog from "@/components/laser/AddMachineDialog";
import CalibrationDialog from "@/components/laser/CalibrationDialog";
import CalibrationCard from "@/components/laser/CalibrationCard";
import DatabaseCheck from "@/components/laser/DatabaseCheck";
import AdminCatalog from "@/components/laser/AdminCatalog";
import TeamTestsNotice from "@/components/laser/TeamTestsNotice";
import { MachineArt, card, greenBtn, ghostBtn } from "@/components/laser/LaserParts";

const Chip = ({ on, children, ...rest }) => (
  <button {...rest} style={{ padding: "7px 16px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: on ? "#4CCC06" : "#242424", color: on ? "#000" : "#FFFFFF", border: `1px solid ${on ? "#4CCC06" : "#333"}` }}>{children}</button>
);
const H = ({ children }) => <p style={{ color: "#4CCC06", fontSize: 11, fontWeight: 800, letterSpacing: 1.5 }}>{children}</p>;

const MachineTile = ({ m, catalog, onTest, onRemove }) => (
  <div data-testid={`my-machine-${m.id}`} style={{ ...card, padding: 12 }}>
    <MachineArt item={{ ...(catalog.find((c) => c.id === m.catalog_id) || {}), ...m, id: m.catalog_id }} h={160} />
    <p className="mt-2 text-sm font-bold">{m.nickname || m.name}</p>
    <p className="text-[11px] text-neutral-400">{m.nickname ? m.name : `${m.watts}W ${m.laser_type || "pulsed"}`}{m.serial ? ` · SN ${m.serial}` : ""}</p>
    <p data-testid={`my-machine-tests-${m.id}`} className="mt-1 text-[11px]" style={{ color: "#4CCC06" }}>{m.test_count} test piece{m.test_count === 1 ? "" : "s"}</p>
    <div className="mt-2 flex gap-2">
      <button data-testid={`machine-calibrate-${m.id}`} onClick={() => onTest(m)} style={{ ...greenBtn, padding: "8px 12px", fontSize: 12 }} className="flex items-center gap-1"><FlaskConical size={14} />Test piece</button>
      <button data-testid={`machine-remove-${m.id}`} aria-label="Remove machine" onClick={() => onRemove(m)} style={ghostBtn}><Trash2 size={14} /></button>
    </div>
  </div>
);

export default function MachineCalibration() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [machines, setMachines] = useState(null);
  const [tests, setTests] = useState(null);
  const [scope, setScope] = useState("mine");
  const [err, setErr] = useState(null);
  const [adding, setAdding] = useState(false);
  const [testing, setTesting] = useState(null);
  const loadCatalog = () => api("/laser/catalog").then(setCatalog).catch((e) => setErr(e.message));
  const loadMachines = () => api("/laser/machines").then(setMachines).catch((e) => setErr(e.message));
  const loadTests = () => { setTests(null); api(`/laser/calibrations?scope=${scope}`).then(setTests).catch((e) => setErr(e.message)); };
  useEffect(() => { loadCatalog(); loadMachines(); }, []);
  useEffect(loadTests, [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { loadMachines(); loadTests(); };
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); refresh(); } catch (e) { toast.error(e.message); } };
  const removeMachine = (m) => window.confirm(`Remove ${m.nickname || m.name}? Its test pieces stay in the shared database.`) && act(() => api(`/laser/machines/${m.id}`, { method: "DELETE" }), "Machine removed");
  const delTest = (c) => window.confirm("Delete this test piece?") && act(() => api(`/laser/calibrations/${c.id}`, { method: "DELETE" }), "Deleted");
  const active = catalog.filter((c) => c.active !== false);

  return (
    <PortalPage eyebrow="LASER CLEANING SETTINGS" title="Machines & Calibration" testId="machine-calibration-page" width="max-w-6xl" back="/my-presets" backLabel="Cleaning Settings">
      <p className="text-sm text-neutral-400">Add the ALR laser you run, clean a test piece, and lock in the settings that worked. Every test is shared anonymously, so the Cleaning Agent gets more reliable for every franchisee.</p>
      <ErrorNote>{err}</ErrorNote>
      <TeamTestsNotice markSeenOnView />
      <div className="flex items-center justify-between"><H>MY MACHINES</H>
        <button data-testid="add-machine-btn" onClick={() => setAdding(true)} style={greenBtn} className="flex items-center gap-1"><Plus size={15} />Add machine</button>
      </div>
      {!machines ? <Spinner /> : machines.length === 0
        ? <p data-testid="no-machines" className="text-sm text-neutral-400">No machines yet — add the laser you own to start calibrating.</p>
        : <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{machines.map((m) => <MachineTile key={m.id} m={m} catalog={catalog} onTest={setTesting} onRemove={removeMachine} />)}</div>}
      {machines && <DatabaseCheck machines={machines} />}
      <div className="flex flex-wrap items-center gap-2"><H>TEST PIECES</H>
        <span className="ml-auto flex gap-2">
          <Chip on={scope === "mine"} onClick={() => setScope("mine")} data-testid="tests-tab-mine">My tests</Chip>
          <Chip on={scope === "all"} onClick={() => setScope("all")} data-testid="tests-tab-all">All franchisees</Chip>
        </span>
      </div>
      {!tests ? <Spinner /> : tests.length === 0 ? <p data-testid="tests-empty" className="text-sm text-neutral-400">No test pieces yet.</p>
        : tests.map((c) => <CalibrationCard key={c.id} c={c} onDelete={delTest} />)}
      {user.role === "admin" && <AdminCatalog catalog={catalog} onChange={loadCatalog} />}
      <AddMachineDialog open={adding} onOpenChange={setAdding} catalog={active} onAdded={loadMachines} />
      {testing && <CalibrationDialog open={!!testing} onOpenChange={(o) => !o && setTesting(null)} machine={testing} onSaved={refresh} />}
    </PortalPage>
  );
}
